import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { MeetingListResponse, ProjectListResponse } from '../../shared/api-types.js';
import { api } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import { Button, LinkButton, Page, PageHead, formatDateOnly, useIsMobile, useRequest, useToast } from '../ui/index.js';
import { parseTodoFilters, writeTodoFilters, type ProjectFilterOption, type TodoFilters } from './lib/todos.js';
import { OWN_PROJECTS_PATH } from './project-checkboxes.js';
import { AddTodoDialog, TodoFilterBar, TodoTable, useTodoList, type MeetingOption } from './todo-table.js';
import './pages.css';

// The one list of every action item across every meeting (spec §13). Filters, sort and the
// highlighted row all live in the URL, so a filtered view can be sent to somebody and comes back
// the same after a refresh.

const HIGHLIGHT_PARAM = 'highlight';

export default function TodosPage() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const filters = useMemo(() => parseTodoFilters(params), [params]);
  const setFilters = useCallback((next: TodoFilters) => {
    setParams((current) => writeTodoFilters(current, next), { replace: true });
  }, [setParams]);

  const list = useTodoList(filters);
  const [addOpen, setAddOpen] = useState(false);

  // Meeting titles for the structured filter and its chip; a failure here only hides the select.
  const meetings = useRequest<MeetingListResponse>((signal) => api.get<MeetingListResponse>('/api/meetings?sort=started_at&dir=desc', { signal }), []);
  const meetingOptions: MeetingOption[] = useMemo(
    () => (meetings.data?.items ?? []).map((meeting) => ({
      value: meeting.id,
      label: meeting.startedAt ? `${meeting.title} — ${formatDateOnly(meeting.startedAt.slice(0, 10))}` : meeting.title,
    })),
    [meetings.data],
  );
  // The reader's own live projects for the Project filter (ADR 0017), asked for only under the floor; a failure here is
  // said beside the select with a retry (Phase 1c D18), and a chip still names its project from the list's own answer.
  const projectsOn = me.integrations.projects === 'on';
  const projects = useRequest<ProjectListResponse | null>(
    (signal) => (projectsOn ? api.get<ProjectListResponse>(OWN_PROJECTS_PATH, { signal }) : Promise.resolve({ ok: true as const, status: 200, data: null })),
    [projectsOn],
  );
  const projectOptions: ProjectFilterOption[] | null = useMemo(
    () => (projects.data ? projects.data.items.map((project) => ({ id: project.id, name: project.name, slot: project.slot })) : null),
    [projects.data],
  );

  const counts = list.request.data ? { matching: list.request.data.matching, total: list.request.data.total } : null;

  // Why: the page's one primary action. On a phone it pins above the tab bar, at the end of the list, instead of sitting
  // in the stacked head (02-patterns §2.1: exactly one primary action on a long list SHOULD; owner's pick C7, conformity
  // 2026-10-05); on desktop it stays in the head. Rendered once, where it shows, so the tab order follows the visual
  // order (01-foundations §7.2) — never a hidden twin.
  const isMobile = useIsMobile();
  const addTodo = <Button variant="primary" icon="plus" block={isMobile} onClick={() => setAddOpen(true)}>Add a to-do</Button>;

  return (
    <Page measure="wide">
      <PageHead title="To-dos" actions={isMobile ? undefined : addTodo} />
      <TodoFilterBar filters={filters} onChange={setFilters} meetingOptions={meetingOptions} projectOptions={projectOptions} projectOptionsFailure={projects.status === 'error' ? projects.error : null} onRetryProjectOptions={projects.reload} list={list} counts={counts} />
      <TodoTable
        caption="To-dos"
        list={list}
        filters={filters}
        onFiltersChange={setFilters}
        highlightParam={HIGHLIGHT_PARAM}
        empty={{
          title: 'No to-dos yet',
          description: 'To-dos arrive as your Fellow meetings are imported and summarised.',
          action: (
            <>
              <Button variant="secondary" icon="plus" onClick={() => setAddOpen(true)}>Add a to-do</Button>
              <LinkButton to={PATHS.connections} variant="ghost" icon="plug">Open Connections</LinkButton>
            </>
          ),
        }}
      />
      {isMobile ? <div className="page-pinned-action">{addTodo}</div> : null}
      <AddTodoDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(created) => {
          setAddOpen(false);
          toast.success('To-do added');
          // Why: the new row is identified in place (06-modules §5.6) — the highlight param scrolls
          // to it once the list has reloaded, and widens the status filter if it is not open.
          setParams((current) => {
            const next = writeTodoFilters(current, { ...filters, q: '' });
            next.set(HIGHLIGHT_PARAM, created.id);
            return next;
          }, { replace: true });
          list.request.reload();
        }}
      />
    </Page>
  );
}
